import { useState } from 'react';
import useBulkCreateProjectDetailsPost from '../../hooks/posts/useBulkCreateProjectDetailsPost';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { IndexTabs, TabPanel } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import styles from './BulkCreatePostModal.module.css';
import { CurrencyInput } from '@round/whitelabel-ui';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { PostUrlLink, getPostUrl } from './shared/PostUrlLink';
import { ExpandableHeader } from './shared/ExpandableHeader';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import BulkUploadReport from './BulkUploadReport/BulkUploadReport';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    campaign: creatorbase.Campaign | null | undefined;
};

type ModalTab = 'upload' | 'report';

type SubmissionResult = {
    created: (creatorbase.Post & { requested_url?: string })[];
    errors: creatorbase.BulkCreatePostError[];
    duplicated: (creatorbase.Post & { requested_url?: string })[];
};

const MAX_RETRIES_PER_FAILED_URL = 2;

const formatErrorMessages = (error: creatorbase.BulkCreatePostError): string[] => {
    const errorMessages: string[] = [];

    if (error.errors) {
        Object.entries(error.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
                messages.forEach((msg) => {
                    // A new Error message was added to the errors object when the video is not found on store. We do not
                    // want to display the field name in the error message.
                    if (field === 'non_field_errors' || field === 'Error') {
                        errorMessages.push(msg);
                    } else {
                        errorMessages.push(`${field}: ${msg}`);
                    }
                });
            } else {
                errorMessages.push(String(messages));
            }
        });
    }

    return errorMessages;
};

const pluraliseErrorMessages = (messages: string[], urlCount: number): string[] => {
    if (urlCount <= 1) {
        return messages;
    }

    const replacements: Record<string, string> = {
        url: 'urls',
        video: 'videos',
        post: 'posts',
        user: 'users',
        error: 'errors',
        field: 'fields',
        this: 'these',
        that: 'those',
        it: 'they',
    };

    return messages.map((msg) =>
        Object.entries(replacements).reduce((text, [singular, plural]) => {
            const regex = new RegExp(`\\b${singular}\\b`, 'gi');

            return text.replace(regex, (match) => {
                // For URL, we want to ensure "URL" is replaced with "URLs" (not "Urls")
                if (singular === 'url' && match === 'URL') return 'URLs';
                return match[0] === match[0].toUpperCase() ? plural.charAt(0).toUpperCase() + plural.slice(1) : plural;
            });
        }, msg)
    );
};

const formatUrlMessage = (count: number, baseVerb: string, action: string): string => {
    const plural = count === 1 ? '' : 's';

    // Only add "was/were" if the action is "duplicated"
    const verb = action === 'duplicated' ? (count === 1 ? 'was' : 'were') : '';

    // Build message parts and filter out empty strings to avoid extra spaces
    const parts = [count, `URL${plural}`, verb, baseVerb, action].filter((part) => part !== '');
    return parts.join(' ');
};

const groupErrorsByMessages = (errors: creatorbase.BulkCreatePostError[]) => {
    const groups = new Map<
        string,
        {
            messages: string[];
            identifiers: string[];
        }
    >();

    errors.forEach((error) => {
        const messages = formatErrorMessages(error);
        const key = messages.join('\n');

        const existing = groups.get(key);
        if (existing) {
            if (error.identifier && !existing.identifiers.includes(error.identifier)) {
                existing.identifiers.push(error.identifier);
            }
            return;
        }

        groups.set(key, {
            messages,
            identifiers: error.identifier ? [error.identifier] : [],
        });
    });

    return Array.from(groups.values()).map((group) => ({
        ...group,
        messages: pluraliseErrorMessages(group.messages, group.identifiers.length),
    }));
};

const isValidUrl = (line: string): boolean => {
    const trimmed = line.trim();
    if (trimmed.length === 0) {
        return false;
    }
    // Check if the line is a valid URL
    const urlPattern = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i;
    return urlPattern.test(trimmed);
};

const BulkCreatePostModal = ({ isOpen, closeModal, campaign }: Props) => {
    const { bulkCreatePosts } = useBulkCreateProjectDetailsPost();
    const { getCurrencyById } = useCurrencies();
    const { isEnabled } = useFeatureFlags();
    const isAsyncUploadEnabled = isEnabled('bulk_post_upload_async');
    const [urls, setUrls] = useState('');
    const [cost, setCost] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState<SubmissionResult | null>(null);
    const [isSuccessExpanded, setIsSuccessExpanded] = useState(false);
    const [isErrorExpanded, setIsErrorExpanded] = useState(false);
    const [rowLimitError, setRowLimitError] = useState(false);
    const [isDuplicatedExpanded, setIsDuplicatedExpanded] = useState(false);
    const [retryAllAttempts, setRetryAllAttempts] = useState(0);
    const [activeTab, setActiveTab] = useState<ModalTab>('upload');
    const MAX_URLS_PER_SUBMISSION = 100;

    const currency = campaign?.currency_id ? getCurrencyById(campaign.currency_id) : undefined;

    // Calculate URL count from current input (only count valid URLs)
    const urlCount = urls
        .split('\n')
        .map((url) => url.trim())
        .filter((url) => url.length > 0 && isValidUrl(url)).length;

    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const textarea = e.target;
        const inputValue = textarea.value;

        // Async uploads have no FE cap — backend accepts unbounded batches.
        if (isAsyncUploadEnabled) {
            setRowLimitError(false);
            setUrls(textarea.value);
            return;
        }

        // Count valid URLs to check limit
        const validUrlLines = inputValue
            .split(/\r?\n/)
            .map((url) => url.trim())
            .filter((url) => url.length > 0 && isValidUrl(url));

        if (validUrlLines.length > MAX_URLS_PER_SUBMISSION) {
            setRowLimitError(true);
            // Keep only the first 100 non-empty lines
            const limitedLines: string[] = [];
            let nonEmptyCount = 0;
            for (const line of validUrlLines) {
                if (line.trim().length > 0) {
                    if (nonEmptyCount < MAX_URLS_PER_SUBMISSION) {
                        limitedLines.push(line);
                        nonEmptyCount++;
                    }
                    // Skip lines beyond 100
                } else {
                    // Keep empty lines if we haven't reached 100 non-empty lines yet
                    if (nonEmptyCount < MAX_URLS_PER_SUBMISSION) {
                        limitedLines.push(line);
                    }
                }
            }
            setUrls(limitedLines.join('\n'));
            return;
        }
        setRowLimitError(false);
        setUrls(textarea.value);
    };

    const submitUrls = async (urlList: string[]) => {
        if (!campaign) {
            return;
        }

        setIsSubmitting(true);
        try {
            const payload: creatorbase.BulkCreatePostsRequestData = {
                posts: urlList.map((identifier) => ({
                    identifier,
                    campaign_id: campaign.id,
                    ...(cost && { cost: parseFloat(cost) }),
                })),
                ...(isAsyncUploadEnabled && { async: true, project_id: campaign.project_id }),
            };

            const response = await bulkCreatePosts(payload);

            if (response.status === 202) {
                showNotification(
                    `Upload started — processing ${response.data.total_items} link(s) in the background`,
                    'info'
                );
                setUrls('');
                setCost('');
                setActiveTab('report');
                return;
            }

            // Check if response has BulkCreatePostsResponseData regardless of status
            // Even failed requests (like 400) might have partial results
            const hasResultsData =
                response.data &&
                typeof response.data === 'object' &&
                ('created' in response.data || 'duplicated' in response.data || 'errors' in response.data);

            if (hasResultsData) {
                const data = response.data as creatorbase.BulkCreatePostsResponseData;

                // Reset result expanders whenever we show a new result set (Ensure they are closed when a new result set is shown)
                setIsSuccessExpanded(false);
                setIsDuplicatedExpanded(false);
                setIsErrorExpanded(false);

                // Always replace result section with the latest response results
                setResult({
                    created: data.created || [],
                    errors: data.errors || [],
                    duplicated: data.duplicated || [],
                });

                const createdCount = data.created_count || 0;
                const errorCount = data.error_count || 0;
                const duplicatedCount = data.duplicated_count || 0;

                // Show detailed notification based on what actually happened
                if (createdCount === urlList.length && errorCount === 0) {
                    showNotification(`Successfully created ${createdCount} post(s)`, 'info');
                } else if (createdCount > 0) {
                    const parts: string[] = [`${createdCount} created`];
                    if (duplicatedCount > 0) {
                        parts.push(`${duplicatedCount} duplicated`);
                    }
                    if (errorCount > 0) {
                        parts.push(`${errorCount} failed`);
                    }
                    showNotification(`Partial success: ${parts.join(', ')}`, 'info');
                } else if (errorCount > 0) {
                    showNotification(`All ${errorCount} post(s) failed to create`, 'error');
                } else {
                    showNotification('No posts were created', 'error');
                }
            } else if (response.status === 400) {
                // If it's a 400 and no results data, show validation error
                showNotification('Validation error. Please check your input.', 'error');
            } else {
                // For other errors without results data, show generic error
                showNotification('Could not create posts', 'error');
            }
        } catch (error) {
            showNotification('Could not create posts', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        if (!campaign) {
            return;
        }

        // New submission: reset retry state
        setRetryAllAttempts(0);

        const urlList = urls
            .split('\n')
            .map((url) => url.trim())
            .filter((url) => url.length > 0 && isValidUrl(url));

        if (urlList.length === 0) {
            showNotification('Please enter at least one URL', 'error');
            return;
        }

        if (!isAsyncUploadEnabled && urlList.length > MAX_URLS_PER_SUBMISSION) {
            showNotification('Maximum 100 URLs allowed per submission', 'error');
            return;
        }

        await submitUrls(urlList);
    };

    const retryFailed = async (identifiers: string[]) => {
        if (!campaign) {
            return;
        }
        // If the user has already retried the maximum number of times, don't retry again.
        if (retryAllAttempts >= MAX_RETRIES_PER_FAILED_URL) {
            return;
        }

        const uniqueIdentifiers = Array.from(new Set(identifiers.map((id) => id.trim()).filter(Boolean)));
        if (uniqueIdentifiers.length === 0) {
            return;
        }

        // Increment attempts immediately to enforce cap even if clicked quickly.
        setRetryAllAttempts((prev) => prev + 1);

        await submitUrls(uniqueIdentifiers);
    };

    const handleClose = () => {
        // Prevent closing while submitting
        if (isSubmitting) {
            return;
        }
        setUrls('');
        setCost('');
        setResult(null);
        setRowLimitError(false);
        setIsSuccessExpanded(false);
        setIsErrorExpanded(false);
        setIsDuplicatedExpanded(false);
        setRetryAllAttempts(0);
        setActiveTab('upload');
        closeModal();
    };

    const handleReset = () => {
        setUrls('');
        setCost('');
        setResult(null);
        setIsSuccessExpanded(false);
        setIsErrorExpanded(false);
        setIsDuplicatedExpanded(false);
        setRowLimitError(false);
        setRetryAllAttempts(0);
    };

    return (
        <Modal
            className={styles.modal}
            closeOnOverlayClick={!isSubmitting}
            closeOnEscape={!isSubmitting}
            isOpen={isOpen}
            onClose={handleClose}
        >
            <Modal.Header>
                <Modal.Title>Upload post URLs </Modal.Title>
                <p className={styles.helperText}>
                    {isAsyncUploadEnabled
                        ? 'Paste post URLs to upload. Large batches are processed in the background.'
                        : `Please enter a maximum of ${MAX_URLS_PER_SUBMISSION} URLs per submission.`}
                </p>
            </Modal.Header>
            <Modal.Body>
                <IndexTabs activeTab={activeTab} onChange={(tab) => setActiveTab(tab)}>
                    <IndexTabs.Menu className={styles.tabMenu}>
                        <IndexTabs.Item name="upload">Upload</IndexTabs.Item>
                        {isAsyncUploadEnabled && <IndexTabs.Item name="report">Report</IndexTabs.Item>}
                    </IndexTabs.Menu>
                    <TabPanel name="upload">
                        {!result ? (
                            <div className={styles.inputSection}>
                                {campaign?.is_post_cost_editable && (
                                    <div className={styles.costInputContainer}>
                                        <label htmlFor="cost" className={styles.label}>
                                            Default cost for all posts
                                        </label>
                                        <CurrencyInput
                                            id="cost"
                                            className={styles.costInput}
                                            value={cost}
                                            onValueChange={(value) => setCost(value ?? '')}
                                            placeholder="Enter cost for all posts..."
                                            prefix={currency?.symbol ?? ''}
                                            groupSeparator=","
                                            decimalSeparator="."
                                            decimalsLimit={2}
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                )}
                                <div className={styles.labelContainer}>
                                    <label htmlFor="urls" className={styles.label}>
                                        Post URLs
                                    </label>
                                    <span className={styles.urlCount}>
                                        {urlCount} {urlCount === 1 ? 'URL' : 'URLs'}
                                        {!isAsyncUploadEnabled && urlCount > 0 && ` / ${MAX_URLS_PER_SUBMISSION}`}
                                    </span>
                                </div>
                                {rowLimitError && (
                                    <div className={styles.errorMessage}>
                                        You can upload a maximum of {MAX_URLS_PER_SUBMISSION} URLs at a time.
                                    </div>
                                )}
                                <textarea
                                    id="urls"
                                    className={`${styles.textarea} ${rowLimitError ? styles.textareaError : ''}`}
                                    value={urls}
                                    onChange={handleInput}
                                    placeholder="Paste list of urls here..."
                                    rows={12}
                                    disabled={isSubmitting}
                                />
                            </div>
                        ) : (
                            <div className={styles.resultsSection}>
                                {result.created.length > 0 && (
                                    <div className={styles.successResultGroup}>
                                        <ExpandableHeader
                                            isExpanded={isSuccessExpanded}
                                            onToggle={() => setIsSuccessExpanded(!isSuccessExpanded)}
                                            headerText={formatUrlMessage(
                                                result.created.length,
                                                'successfully',
                                                'uploaded'
                                            )}
                                            className={styles.successResultHeader}
                                        />
                                        {isSuccessExpanded && (
                                            <div className={styles.successResultList}>
                                                {result.created.map((post) => (
                                                    <p key={post.id} className={styles.resultUrl}>
                                                        <PostUrlLink
                                                            url={getPostUrl(post)}
                                                            className={styles.urlLink}
                                                        />
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {result.duplicated.length > 0 && (
                                    <div className={styles.duplicatedResultGroup}>
                                        <ExpandableHeader
                                            isExpanded={isDuplicatedExpanded}
                                            onToggle={() => setIsDuplicatedExpanded(!isDuplicatedExpanded)}
                                            headerText={formatUrlMessage(result.duplicated.length, '', 'duplicated')}
                                            className={styles.duplicatedResultHeader}
                                        />
                                        {isDuplicatedExpanded && (
                                            <div className={styles.duplicatedResultList}>
                                                {result.duplicated.map((post) => (
                                                    <p key={post.id} className={styles.resultUrl}>
                                                        <PostUrlLink
                                                            url={getPostUrl(post)}
                                                            className={styles.urlLink}
                                                        />
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {result.errors.length > 0 && (
                                    <div className={styles.failedResultGroup}>
                                        <div className={styles.failedHeaderRow}>
                                            <ExpandableHeader
                                                isExpanded={isErrorExpanded}
                                                onToggle={() => setIsErrorExpanded(!isErrorExpanded)}
                                                headerText={formatUrlMessage(
                                                    result.errors.length,
                                                    'failed to',
                                                    'upload'
                                                )}
                                                className={styles.failedResultHeader}
                                            />
                                            {isErrorExpanded && (
                                                <Button
                                                    className={styles.retryButton}
                                                    appearance="ghost"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        retryFailed(result.errors.map((er) => er.identifier));
                                                    }}
                                                    disabled={
                                                        isSubmitting || retryAllAttempts >= MAX_RETRIES_PER_FAILED_URL
                                                    }
                                                    isLoading={isSubmitting}
                                                    aria-label="Retry all failed"
                                                    hint="Retry all failed"
                                                >
                                                    Retry
                                                </Button>
                                            )}
                                        </div>
                                        {isErrorExpanded && (
                                            <div className={styles.failedResultList}>
                                                {groupErrorsByMessages(result.errors).map((group) => (
                                                    <div
                                                        key={group.messages.join('|') || 'unknown-error'}
                                                        className={styles.failedErrorGroup}
                                                    >
                                                        <div className={styles.failedErrorHeader}>
                                                            {group.messages.length > 0 ? (
                                                                group.messages.map((msg, msgIndex) => (
                                                                    <p key={msgIndex} className={styles.errorMessage}>
                                                                        {msg}
                                                                    </p>
                                                                ))
                                                            ) : (
                                                                <p className={styles.errorMessage}>Unknown error</p>
                                                            )}
                                                        </div>
                                                        <div className={styles.failedErrorUrls}>
                                                            {group.identifiers.map((identifier) => (
                                                                <p key={identifier} className={styles.resultUrl}>
                                                                    <PostUrlLink
                                                                        url={identifier}
                                                                        className={styles.urlLink}
                                                                    />
                                                                </p>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </TabPanel>
                    <TabPanel name="report">
                        <BulkUploadReport projectId={campaign?.project_id} campaignId={campaign?.id} />
                    </TabPanel>
                </IndexTabs>
            </Modal.Body>
            {activeTab === 'upload' && (
                <Modal.Actions>
                    {!result ? (
                        <>
                            <Button
                                appearance="primary"
                                isLoading={isSubmitting}
                                disabled={!urls.trim() || isSubmitting}
                                onClick={handleSubmit}
                            >
                                Upload post URLs
                            </Button>
                            <Button appearance="ghost" onClick={handleClose} disabled={isSubmitting}>
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button appearance="primary" onClick={handleReset} disabled={isSubmitting}>
                                Upload more post URLs
                            </Button>
                            <Button appearance="ghost" onClick={handleClose} disabled={isSubmitting}>
                                Close
                            </Button>
                        </>
                    )}
                </Modal.Actions>
            )}
        </Modal>
    );
};

export default BulkCreatePostModal;
